import ElakayImage from '../../components/images/ElakayImage';
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Clock, Star, TrendingUp, FileQuestion } from "lucide-react";
import Layout from "../Layout/Layout";
import { isAuthenticated } from "../auth";
import { getAllTutorials, getTutorials } from "../auth/apiHowWeDoIt";
import HowWeDoItSidebar from "../pageInside/howWeDoIt/HowWeDoItSidebar";
import { normalizeHowWeDoItDocument } from "../pageInside/howWeDoIt/howWeDoItCodeDocument";
import { resolveHowWeDoItMediaUrl } from "../pageInside/howWeDoIt/howWeDoItMediaUrl";
import { getEffectiveWallRoleName, getVisibleWallRoles } from "../pageInside/Rolle";
import { roleLabel, languageLabel, TUTORIAL_LANGUAGES } from "../pageInside/howWeDoIt/howWeDoItLabels";
import { buildTutorialsHomeSeo, useDocumentSeo } from "../pageInside/howWeDoIt/howWeDoItSeo";
import { elakayAutoText } from "../pageInside/dataElakay";
import "../pageInside/howWeDoIt/howWeDoItDesignSystem.css";
import "./howWeDoIt.css";

const ROLE_CHOICES = ["Royalty", "Seller", "Provider", "Associate", "Investment", "Family", "Administrator"];

// Signal deja recupere (featured / tri "popular") mais jusque-la jamais
// affiche a l'ecran -- badge purement presentationnel, aucune nouvelle
// donnee. `badge` vient du contexte de la rangee (CardRow), pas d'un champ
// par-carte, car "populaire" est une propriete du tri demande au serveur,
// pas un champ persiste sur le tutoriel.
const BADGE_CONFIG = {
  featured: { get label() { return elakayAutoText("h_a98ccedb028be5", "A la une"); }, icon: Star },
  popular: { get label() { return elakayAutoText("h_e67f85051b77e4", "Populaire"); }, icon: TrendingUp },
};

const TutorialCard = ({ tutorial, badge }) => {
  const badgeConfig = badge && BADGE_CONFIG[badge];
  const BadgeIcon = badgeConfig?.icon;
  return (
    <Link to={`/how-we-do-it/${tutorial.slug}`} className="hwdi-card">
      <div className="hwdi-card-image">
        {tutorial.coverImage?.url ? <ElakayImage preset="content" src={resolveHowWeDoItMediaUrl(tutorial.coverImage.url)} alt={tutorial.coverImage.alt || tutorial.title} loading="lazy" /> : <div className="hwdi-card-image-placeholder" />}
        {badgeConfig && (
          <span className="hwdi-card-badge">
            <BadgeIcon size={12} /> {elakayAutoText(`howWeDoIt_landing_badge_${badge}`, badgeConfig.label)}
          </span>
        )}
      </div>
      <div className="hwdi-card-body">
        {tutorial.category?.name && <span className="hwdi-card-category">{tutorial.category.name}</span>}
        <h3>{tutorial.title}</h3>
        {tutorial.shortDescription && <p>{tutorial.shortDescription}</p>}
        <span className="hwdi-card-meta"><Clock size={12} /> {tutorial.estimatedReadingTime || 1} {elakayAutoText("h_b6c935d4f3c7b2", "min")}</span>
      </div>
    </Link>
  );
};

const CardRow = ({ title, items, badge }) => {
  if (!items.length) return null;
  return (
    <section className="hwdi-section">
      <h2>{title}</h2>
      <div className="hwdi-card-grid">
        {items.map((t) => <TutorialCard key={t._id} tutorial={t} badge={badge} />)}
      </div>
    </section>
  );
};

const SkeletonCard = () => (
  <div className="hwdi-card hwdi-card-skeleton" aria-hidden="true">
    <div className="hwdi-skeleton hwdi-card-image" />
    <div className="hwdi-card-body">
      <span className="hwdi-skeleton" style={{ width: "40%", height: 10 }} />
      <span className="hwdi-skeleton" style={{ width: "80%", height: 16 }} />
      <span className="hwdi-skeleton" style={{ width: "95%", height: 12 }} />
    </div>
  </div>
);

const HowWeDoIt = ({ infoSource }) => {
  const auth = isAuthenticated();
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [language, setLanguage] = useState("");
  const [role, setRole] = useState("");

  const [searchResults, setSearchResults] = useState(null);
  const [popular, setPopular] = useState([]);
  const [recent, setRecent] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [sidebarTutorials, setSidebarTutorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const filtersActive = !!(search || category || language || role);

  // "Role" here filters tutorials by target-audience tag. Royalty/Seller/
  // Provider are "become this" growth tiers, always offered to everyone
  // (including logged-out visitors); Associate/Investment/Administrator/
  // Family stay gated behind the user's own rank -- see getVisibleWallRoles.
  const currentWallRole = getEffectiveWallRoleName(infoSource);
  const visibleWallRoles = useMemo(() => getVisibleWallRoles(currentWallRole), [currentWallRole]);

  useEffect(() => {
    if (role && !visibleWallRoles.includes(role)) setRole("");
  }, [role, visibleWallRoles]);

  useEffect(() => {
    const loadDefaults = async () => {
      setLoading(true);
      setError("");
      try {
        // The sidebar directory reuses the same public, already-filtered
        // endpoint (limit=300) that the tutorial detail page relies on for
        // its own nav -- purely an extra read for display, no new endpoint.
        const [popularData, recentData, recommendedData, directoryData] = await Promise.all([
          getTutorials({ limit: 4, sort: "popular" }, auth?.token),
          getTutorials({ limit: 4, sort: "recent" }, auth?.token),
          getTutorials({ limit: 4, featured: "true" }, auth?.token),
          getAllTutorials({}, auth?.token),
        ]);
        setCategories(popularData.categories || []);
        setPopular((popularData.tutorials || []).map(normalizeHowWeDoItDocument));
        setRecent((recentData.tutorials || []).map(normalizeHowWeDoItDocument));
        setRecommended((recommendedData.tutorials || []).map(normalizeHowWeDoItDocument));
        setSidebarTutorials((directoryData.tutorials || []).map(normalizeHowWeDoItDocument));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    loadDefaults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!filtersActive) {
      setSearchResults(null);
      return undefined;
    }
    const timeout = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await getTutorials({ limit: 24, q: search, category, language, role }, auth?.token);
        setSearchResults((data.tutorials || []).map(normalizeHowWeDoItDocument));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, category, language, role]);

  // The role filter is applied by the server (tutorials without a role
  // restriction stay listed), so the results and their count are complete.
  const roleFilteredResults = searchResults;

  useDocumentSeo(
    buildTutorialsHomeSeo({
      title: elakayAutoText("howWeDoIt_mainTitle", "How We Do It in Elakay"),
      description: elakayAutoText("howWeDoIt_landing_subtitle", "Des tutoriels complets pour maitriser chaque fonctionnalite d'Elakay."),
    }),
    infoSource?.languageAPI
  );

  return (
    <Layout infoSource={infoSource} loading={false} SliderPunShow={false} outOffPage={false} showFilterMenu={false} enableBodyParticles={false} enableFloatingSquares={false}>
      <main className="hwdi-public-page hwdi-scope" style={infoSource?.styleNav}>
        <header className="hwdi-hero">
          <div className="hwdi-hero-inner">
            <span className="hwdi-hero-eyebrow">{elakayAutoText("howWeDoIt_landing_eyebrow", "Centre d'aide Elakay")}</span>
            <h1>{elakayAutoText("howWeDoIt_mainTitle", "How We Do It in Elakay")}</h1>
            <p>{elakayAutoText("howWeDoIt_landing_subtitle", "Des tutoriels complets pour maitriser chaque fonctionnalite d'Elakay.")}</p>
            <div className="hwdi-search-bar">
              <Search size={16} />
              <input
                type="search"
                placeholder={elakayAutoText("howWeDoIt_landing_searchPlaceholder", "Rechercher un tutoriel...")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label={elakayAutoText("howWeDoIt_landing_searchPlaceholder", "Rechercher un tutoriel...")}
              />
            </div>
          </div>
        </header>

        <div className="hwdi-public-shell">
          <div className="hwdi-public-layout">
            <HowWeDoItSidebar categories={categories} tutorials={sidebarTutorials} currentSlug={null} />

            <div className="hwdi-public-main">
              <div className="hwdi-category-tabs">
                <button type="button" className={!category ? "active" : ""} onClick={() => setCategory("")}>
                  {elakayAutoText("howWeDoIt_landing_allCategories", "Toutes")}
                </button>
                {categories.map((cat) => (
                  <button key={cat._id} type="button" className={category === cat._id ? "active" : ""} onClick={() => setCategory(cat._id)}>
                    {cat.icon} {cat.name}
                  </button>
                ))}
              </div>

              <div className="hwdi-filter-row">
                <select value={language} onChange={(e) => setLanguage(e.target.value)} aria-label={elakayAutoText("howWeDoIt_dashboard_col_language", "Langue")}>
                  <option value="">{elakayAutoText("howWeDoIt_dashboard_allLanguages", "Toutes langues")}</option>
                  {TUTORIAL_LANGUAGES.map((code) => <option key={code} value={code}>{languageLabel(code)}</option>)}
                </select>
                <select value={role} onChange={(e) => setRole(e.target.value)} aria-label={elakayAutoText("howWeDoIt_dashboard_col_roles", "Roles")}>
                  <option value="">{elakayAutoText("howWeDoIt_landing_allRoles", "Tous roles")}</option>
                  {ROLE_CHOICES
                    .filter((value) => visibleWallRoles.includes(value))
                    .map((value) => <option key={value} value={value}>{roleLabel(value)}</option>)}
                </select>
              </div>

              {error && <div className="hwdi-public-message error">{error}</div>}
              {loading && (
                <div className="hwdi-card-grid" aria-busy="true" aria-label={elakayAutoText("howWeDoIt_loading", "Chargement...")}>
                  {[0, 1, 2, 3].map((i) => <SkeletonCard key={i} />)}
                </div>
              )}

              {!loading && filtersActive && (
                <section className="hwdi-section">
                  <h2>{elakayAutoText("howWeDoIt_landing_results", "Resultats")}</h2>
                  {roleFilteredResults && roleFilteredResults.length > 0 ? (
                    <div className="hwdi-card-grid">
                      {roleFilteredResults.map((t) => <TutorialCard key={t._id} tutorial={t} />)}
                    </div>
                  ) : (
                    <div className="hwdi-public-empty">
                      <FileQuestion size={32} className="hwdi-public-empty-icon" />
                      <p>{elakayAutoText("howWeDoIt_landing_noResults", "Aucun tutoriel trouve.")}</p>
                    </div>
                  )}
                </section>
              )}

              {!loading && !filtersActive && (
                <>
                  <CardRow title={elakayAutoText("howWeDoIt_landing_recommended", "Recommandes")} items={recommended} badge="featured" />
                  <CardRow title={elakayAutoText("howWeDoIt_landing_popular", "Populaires")} items={popular} badge="popular" />
                  <CardRow title={elakayAutoText("howWeDoIt_landing_recent", "Recents")} items={recent} />
                </>
              )}
            </div>
          </div>
        </div>
      </main>
    </Layout>
  );
};

export default HowWeDoIt;
